import React, { useState, useEffect } from 'react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { motion, AnimatePresence } from 'motion/react';
import { Check, ChevronRight, ChevronLeft, Building2, User, Users } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Turnstile } from '@/components/ui/Turnstile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRegion } from '@/contexts/RegionContext';

const COUNTRIES = [
  { code: 'ZA', name: 'South Africa' },
  { code: 'NA', name: 'Namibia' },
  { code: 'BW', name: 'Botswana' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'IE', name: 'Ireland' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SG', name: 'Singapore' },
  { code: 'BE', name: 'Belgium' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'PT', name: 'Portugal' },
  { code: 'GR', name: 'Greece' },
  { code: 'IN', name: 'India' },
  { code: 'KE', name: 'Kenya' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'GH', name: 'Ghana' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'JP', name: 'Japan' },
  { code: 'HK', name: 'Hong Kong' },
];

const VALID_PRODUCTS = [
  'practitioner-certification',
  'team-facilitator-certification',
  'certification-bundle'
] as const;

type ValidProduct = typeof VALID_PRODUCTS[number];

const PRODUCT_LABELS: Record<string, string> = {
  'practitioner-certification': 'FIVE LENS Practitioner Certification (Level 1)',
  'team-facilitator-certification': 'FIVE LENS Team Facilitator Certification (Level 2)',
  'certification-bundle': 'Practitioner + Team Facilitator Bundle',
};

export function RegistrationPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { currency, vatApplies, isZar, regionCode } = useRegion();

  const productParam = searchParams.get('product') || '';
  const isPreselectedCourse = VALID_PRODUCTS.includes(productParam as ValidProduct);
  const [step, setStep] = useState(() => (isPreselectedCourse ? 2 : 1));
  const [isChangingCourse, setIsChangingCourse] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [formData, setFormData] = useState({
    // Product
    product: isPreselectedCourse ? productParam : '',
    hasCompletedLevel1: productParam === 'certification-bundle',

    // Step 1
    role: isPreselectedCourse ? 'practitioner' : '',
    interest: isPreselectedCourse ? 'certification' : '',
    
    // Step 2: Personal
    firstName: '',
    lastName: '',
    gender: '',
    email: '',
    phone: '',
    idNumber: '',
    occupation: '',
    
    // Step 3: Billing & Address
    company: '',
    vatNumber: '',
    country: '',
    billingAddress: '',
    
    // Step 4: Certification Details
    cohortDate: 'Preferred start: next available intake',
    preferredMonth: '',
    coachingQualifications: '',
    facilitationTraining: '',
    facilitatingExperience: '',
    enneagramTraining: '',
    enneagramLevel: '',
    reasonForRegistering: '',
    source: '',
    referredBy: '',
    newsletter: true,
    requirementsAccepted: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState<boolean>(false);

  useEffect(() => {
    const p = searchParams.get('product');
    if (p && VALID_PRODUCTS.includes(p as ValidProduct)) {
      setFormData(prev => ({
        ...prev,
        product: p,
        role: 'practitioner',
        interest: 'certification',
        hasCompletedLevel1: p === 'certification-bundle' ? true : prev.hasCompletedLevel1
      }));
    } else if (p) {
      setFormData(prev => ({
        ...prev,
        product: p
      }));
    }
  }, [searchParams]);

  const currentProduct = formData.product || searchParams.get('product') || '';
  const isTeamFacilitator = currentProduct === 'team-facilitator-certification';

  const handleSwitchToBundle = () => {
    setSearchParams({ product: 'certification-bundle' });
    setFormData(prev => ({
      ...prev,
      product: 'certification-bundle',
      hasCompletedLevel1: true,
    }));
    setErrorMessage(null);
  };

  // Consider it a certification application if role is practitioner, interest is certification, or certification product is selected
  const isCertification = formData.role === 'practitioner' || formData.interest === 'certification' || Boolean(currentProduct && (currentProduct.includes('certification') || currentProduct.includes('bundle')));
  const totalSteps = isCertification ? 4 : 3;

  const handleNext = () => {
    setErrorMessage(null);
    if (step < totalSteps) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    setErrorMessage(null);
    if (step > 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setStep(step - 1);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: (e.target as HTMLInputElement).checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (isTeamFacilitator && !formData.hasCompletedLevel1) {
      setErrorMessage(t('registration.level1PrereqNotice', 'Level 2 requires Level 1. You can enrol in the Practitioner + Team Facilitator Bundle instead.'));
      return;
    }

    if (step === 2 && isCertification) {
      const trimmedOcc = (formData.occupation || '').trim();
      if (!trimmedOcc || trimmedOcc.length < 2) {
        setErrorMessage('Please enter your occupation.');
        return;
      }
      if (trimmedOcc.length > 100) {
        setErrorMessage('Occupation must be 100 characters or fewer.');
        return;
      }
    }

    if (step < totalSteps) {
      handleNext();
      return;
    }

    if (step === totalSteps && !turnstileToken) {
      setErrorMessage('Please complete the Turnstile verification.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          occupation: (formData.occupation || '').trim(),
          currency,
          vatApplies,
          isZar,
          detectedRegion: regionCode,
          turnstileToken,
          company_website: companyWebsite,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data?.error || 'Failed to submit registration. Please email office@enneainternational.com directly.');
      }

      setEmailSent(Boolean(data.emailSent));
      setIsSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Registration submission error:', err);
      setErrorMessage(
        err.message || 'Failed to submit your registration. Please email office@enneainternational.com directly.'
      );
      setTurnstileToken('');
      setResetKey(prev => prev + 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const roles = [
    { id: 'practitioner', label: 'Coach / Practitioner (Certification)', icon: User },
    { id: 'organization', label: 'Corporate / HR Leader (Teams)', icon: Building2 },
    { id: 'individual', label: 'Individual / Personal Growth', icon: Users },
  ];

  return (
    <main className="min-h-screen bg-ground-alt pt-32 pb-24 flex items-center justify-center">
      <div className="max-w-3xl w-full px-6">
        <Breadcrumb items={[{ label: 'Register' }]} />
        <div className="text-center mb-10 mt-8">
          <h1 className="text-4xl md:text-5xl font-serif text-ink mb-4">
            Start Your Journey
          </h1>
          <p className="text-ink/70 text-lg">
            Complete your registration to get started with FIVE LENS.
          </p>
        </div>

        <div className="bg-white border border-ground-deep p-8 md:p-12 shadow-sm relative overflow-hidden">
          
          {/* Progress Bar */}
          {!isSuccess && (
            <div className="absolute top-0 left-0 w-full h-1.5 bg-ground">
              <motion.div 
                className="h-full bg-rule-3"
                initial={{ width: 0 }}
                animate={{ width: `${(step / totalSteps) * 100}%` }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              />
            </div>
          )}

          <AnimatePresence mode="wait">
            {!isSuccess ? (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <form onSubmit={handleSubmit}>
                  {errorMessage && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 text-sm">
                      <p className="font-bold mb-1">Registration Could Not Be Submitted</p>
                      <p>{errorMessage}</p>
                    </div>
                  )}
                  
                  {/* STEP 1: Role Selection */}
                  {step === 1 && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-serif text-ink mb-6">How do you intend to use FIVE LENS?</h2>
                      <div className="grid gap-4">
                        {roles.map((role) => {
                          const Icon = role.icon;
                          const isSelected = formData.role === role.id;
                          return (
                            <button
                              key={role.id}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, role: role.id });
                              }}
                              className={`flex items-center p-4 border text-left transition-all ${
                                isSelected 
                                  ? 'border-rule-3 bg-rule-3/10 shadow-sm' 
                                  : 'border-ground-deep hover:border-rule-3/60 hover:bg-ground/50'
                              }`}
                            >
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 mr-4 transition-colors ${
                                isSelected ? 'bg-rule-3 text-white' : 'bg-ground text-ink/70'
                              }`}>
                                <Icon className="w-6 h-6" />
                              </div>
                              <div>
                                <div className={`font-bold ${isSelected ? 'text-ink' : 'text-ink/70'}`}>
                                  {role.label}
                                </div>
                              </div>
                              <div className="ml-auto">
                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                  isSelected ? 'border-rule-3 bg-rule-3' : 'border-ground-deep'
                                }`}>
                                  {isSelected && <Check className="w-4 h-4 text-ink" />}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {formData.role !== 'practitioner' && formData.role !== '' && (
                        <div className="space-y-2 mt-6">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">Primary Interest</label>
                          <select 
                            required
                            name="interest"
                            value={formData.interest}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none appearance-none"
                          >
                            <option value="" disabled>Select an option...</option>
                            <option value="team">Team Workshop / Effectiveness</option>
                            <option value="personal">Personal Report & Coaching</option>
                            <option value="certification">Wait, I want Practitioner Certification</option>
                            <option value="other">Other / Not Sure Yet</option>
                          </select>
                        </div>
                      )}

                      {/* Step 1 Prerequisite Checkbox and Notice for Level 2 */}
                      {isTeamFacilitator && (
                        <div className="space-y-4 pt-4 border-t border-ground-deep">
                          <label className="flex items-start gap-4 p-4 bg-ground/50 border border-ground-deep cursor-pointer hover:bg-ground transition-colors">
                            <div className="mt-0.5">
                              <input
                                type="checkbox"
                                name="hasCompletedLevel1"
                                checked={formData.hasCompletedLevel1}
                                onChange={handleChange}
                                required
                                className="w-5 h-5 accent-rule-3 cursor-pointer"
                              />
                            </div>
                            <div className="text-sm text-ink">
                              <span className="font-bold block">
                                {t('registration.level1PrereqCheckbox', 'I have completed the FIVE LENS Practitioner Certification (Level 1).')}
                              </span>
                            </div>
                          </label>

                          {!formData.hasCompletedLevel1 && (
                            <div className="p-4 bg-ground border border-ground-deep border-l-4 border-l-rule-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-ink">
                              <p className="font-medium">
                                {t('registration.level1PrereqNotice', 'Level 2 requires Level 1. You can enrol in the Practitioner + Team Facilitator Bundle instead.')}
                              </p>
                              <button
                                type="button"
                                onClick={handleSwitchToBundle}
                                className="shrink-0 bg-ink text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-rule-3 transition-colors"
                              >
                                {t('registration.switchToBundleBtn', 'Enrol in Bundle')}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {step > 1 && VALID_PRODUCTS.includes(currentProduct as ValidProduct) && (
                    <div className="mb-6 p-4 bg-ground/60 border border-ground-deep flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-rule-3 block mb-0.5">
                          Selected Certification
                        </span>
                        <span className="font-serif font-bold text-ink text-base">
                          {PRODUCT_LABELS[currentProduct] || currentProduct}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        {isChangingCourse ? (
                          <select
                            value={currentProduct}
                            onChange={(e) => {
                              const newProduct = e.target.value;
                              setSearchParams({ product: newProduct });
                              setFormData(prev => ({
                                ...prev,
                                product: newProduct,
                                role: 'practitioner',
                                interest: 'certification',
                                hasCompletedLevel1: newProduct === 'certification-bundle' ? true : prev.hasCompletedLevel1
                              }));
                              setIsChangingCourse(false);
                            }}
                            className="text-xs font-semibold bg-white border border-ground-deep px-3 py-1.5 text-ink outline-none"
                          >
                            <option value="practitioner-certification">Practitioner Certification</option>
                            <option value="team-facilitator-certification">Team Facilitator Certification</option>
                            <option value="certification-bundle">Practitioner + Team Facilitator Bundle</option>
                          </select>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setIsChangingCourse(true)}
                            className="text-xs font-bold uppercase tracking-wider text-rule-3 hover:text-ink underline transition-colors"
                          >
                            Change
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP 2: Basic Info */}
                  {step === 2 && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-serif text-ink mb-6">Personal Information</h2>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">First Name</label>
                          <input 
                            required
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                            placeholder="Jane"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">Last Name</label>
                          <input 
                            required
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                            placeholder="Doe"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">Email Address</label>
                          <input 
                            required
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                            placeholder="jane@example.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">Cell Number</label>
                          <input 
                            required
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                            placeholder="+1 (555) 000-0000"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">Gender</label>
                          <select 
                            required
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none appearance-none"
                          >
                            <option value="" disabled>Select Gender...</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">ID / Passport Number</label>
                          <input 
                            required
                            type="text"
                            name="idNumber"
                            value={formData.idNumber}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                            placeholder="Required for registration"
                          />
                        </div>
                      </div>

                      {isCertification && (
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">Occupation</label>
                          <input 
                            required
                            type="text"
                            name="occupation"
                            value={formData.occupation}
                            onChange={handleChange}
                            minLength={2}
                            maxLength={100}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                            placeholder="e.g. Executive coach, HR business partner, Psychologist"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* STEP 3: Company & Billing */}
                  {step === 3 && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-serif text-ink mb-6">Company & Billing</h2>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">
                            Company / Practice Name
                          </label>
                          <input 
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                            placeholder="Optional"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">VAT Number</label>
                          <input 
                            type="text"
                            name="vatNumber"
                            value={formData.vatNumber}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                            placeholder="If applicable"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-bold text-ink uppercase tracking-wider">Country of Residence</label>
                        <select 
                          required
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none appearance-none"
                        >
                          <option value="" disabled>Select Country of Residence...</option>
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.name} ({c.code})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-bold text-ink uppercase tracking-wider">Physical / Postal Address</label>
                        <textarea 
                          required
                          name="billingAddress"
                          value={formData.billingAddress}
                          onChange={handleChange}
                          rows={3}
                          className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                          placeholder="Full address for invoicing..."
                        />
                      </div>
                    </div>
                  )}

                  {/* STEP 4: Certification Registration Details (Conditional) */}
                  {step === 4 && isCertification && (
                    <div className="space-y-8">
                      <div>
                        <h2 className="text-2xl font-serif text-ink mb-2">Certification Registration Details</h2>
                        <p className="text-sm text-ink/70">Tell us a little about your background and preferences.</p>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">Intake Preference</label>
                          <select 
                            required
                            name="cohortDate"
                            value={formData.cohortDate}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none appearance-none"
                          >
                            <option value="Preferred start: next available intake">Preferred start: next available intake</option>
                            <option value="Request specific month">Request specific month</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">
                            Preferred Month <span className="text-xs normal-case font-normal text-ink/60">(Optional)</span>
                          </label>
                          <input 
                            type="text"
                            name="preferredMonth"
                            value={formData.preferredMonth}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                            placeholder="e.g. November, or as soon as possible"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-bold text-ink uppercase tracking-wider">Optional: tell us about your background</label>
                        <textarea 
                          name="coachingQualifications"
                          value={formData.coachingQualifications}
                          onChange={handleChange}
                          rows={3}
                          className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none text-sm"
                          placeholder="Tell us about your background, experience, or what brings you to FIVE LENS (optional)..."
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-bold text-ink uppercase tracking-wider">Optional: tell us about your background in facilitating teams or groups</label>
                        <textarea 
                          name="facilitatingExperience"
                          value={formData.facilitatingExperience}
                          onChange={handleChange}
                          rows={2}
                          className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none text-sm"
                          placeholder="Examples of facilitating / training experience (optional)..."
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-bold text-ink uppercase tracking-wider">Optional: any training in facilitation</label>
                        <textarea 
                          name="facilitationTraining"
                          value={formData.facilitationTraining}
                          onChange={handleChange}
                          rows={2}
                          className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none text-sm"
                          placeholder="Describe your training (optional)..."
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-bold text-ink uppercase tracking-wider">Optional: tell us about your background with the Enneagram</label>
                        <textarea 
                          name="enneagramTraining"
                          value={formData.enneagramTraining}
                          onChange={handleChange}
                          rows={2}
                          className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none text-sm"
                          placeholder="Your Enneagram background (optional)..."
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-bold text-ink uppercase tracking-wider">What is your level of knowledge on the Enneagram? <span className="text-xs normal-case font-normal text-ink/60">(Optional)</span></label>
                        <select 
                          name="enneagramLevel"
                          value={formData.enneagramLevel}
                          onChange={handleChange}
                          className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none appearance-none"
                        >
                          <option value="">Select Level (Optional)...</option>
                          <option value="none">None / New to the Enneagram</option>
                          <option value="beginner">Beginner</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="advanced">Advanced</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-bold text-ink uppercase tracking-wider">What is your reason / purpose for registering for this course?</label>
                        <textarea 
                          required
                          name="reasonForRegistering"
                          value={formData.reasonForRegistering}
                          onChange={handleChange}
                          rows={2}
                          className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none text-sm"
                          placeholder="Your goals..."
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-bold text-ink uppercase tracking-wider">How did you find out about this course?</label>
                        <select 
                          required
                          name="source"
                          value={formData.source}
                          onChange={handleChange}
                          className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none appearance-none"
                        >
                          <option value="" disabled>Select Source...</option>
                          <option value="LinkedIn">LinkedIn</option>
                          <option value="Instagram">Instagram</option>
                          <option value="Facebook">Facebook</option>
                          <option value="Google search">Google search</option>
                          <option value="Referred by someone">Referred by someone</option>
                        </select>
                      </div>

                      {formData.source === 'Referred by someone' && (
                        <div className="space-y-2">
                          <label className="text-sm font-bold text-ink uppercase tracking-wider">If referred by someone, please tell us who?</label>
                          <input 
                            required
                            type="text"
                            name="referredBy"
                            value={formData.referredBy}
                            onChange={handleChange}
                            className="w-full bg-ground border-none p-4 focus:ring-2 focus:ring-rule-3 text-ink outline-none"
                            placeholder="Name of person"
                          />
                        </div>
                      )}

                      <div className="space-y-4 pt-4 border-t border-ground-deep">
                        {isTeamFacilitator && (
                          <div className="p-4 bg-ground border border-ground-deep border-l-4 border-l-rule-3 space-y-3">
                            <label className="flex items-start gap-4 cursor-pointer">
                              <div className="mt-1">
                                <input
                                  type="checkbox"
                                  name="hasCompletedLevel1"
                                  checked={formData.hasCompletedLevel1}
                                  onChange={handleChange}
                                  required
                                  className="w-5 h-5 accent-rule-3 cursor-pointer"
                                />
                              </div>
                              <div className="text-sm text-ink">
                                <span className="font-bold block">
                                  {t('registration.level1PrereqCheckbox', 'I have completed the FIVE LENS Practitioner Certification (Level 1).')}
                                </span>
                              </div>
                            </label>

                            {!formData.hasCompletedLevel1 && (
                              <div className="pt-3 border-t border-ground-deep flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-ink bg-white p-3 border border-ground-deep">
                                <span>
                                  {t('registration.level1PrereqNotice', 'Level 2 requires Level 1. You can enrol in the Practitioner + Team Facilitator Bundle instead.')}
                                </span>
                                <button
                                  type="button"
                                  onClick={handleSwitchToBundle}
                                  className="shrink-0 bg-ink text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-rule-3 transition-colors"
                                >
                                  {t('registration.switchToBundleBtn', 'Enrol in Bundle')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}

                        <label className="flex items-start gap-4 p-4 bg-red-50/50 border border-red-100 cursor-pointer hover:bg-red-50 transition-colors">
                          <div className="mt-1">
                            <input
                              type="checkbox"
                              name="requirementsAccepted"
                              checked={formData.requirementsAccepted}
                              onChange={handleChange}
                              required
                              className="w-5 h-5 accent-red-600 cursor-pointer"
                            />
                          </div>
                          <div className="text-sm text-ink/90">
                            <span className="font-bold text-red-600 block mb-2">I understand and accept the course requirements</span>
                            <ul className="list-disc pl-4 space-y-1 text-xs">
                              <li>Complete Online Learning before the start of Session 1</li>
                              <li>If you are not able to complete this in time you will be referred to our next available course</li>
                              <li>Attend at least 5 out of the 6 sessions</li>
                              <li>If you miss a session you'll need to catch up by reviewing a recording before the next session</li>
                              <li>If 2 or more sessions are missed, you may be asked to re-take the course</li>
                            </ul>
                          </div>
                        </label>

                        <label className="flex items-center gap-4 p-4 bg-ground/30 cursor-pointer hover:bg-ground/50 transition-colors">
                          <input
                            type="checkbox"
                            name="newsletter"
                            checked={formData.newsletter}
                            onChange={handleChange}
                            className="w-5 h-5 accent-rule-3 cursor-pointer"
                          />
                          <span className="text-sm font-bold text-ink">I'd like to receive an occasional newsletter</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  {errorMessage && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 text-red-800 text-sm">
                      <p className="font-bold mb-1">Registration Could Not Be Submitted</p>
                      <p>{errorMessage}</p>
                    </div>
                  )}

                  {step === totalSteps && (
                    <div className="mt-8 pt-6 border-t border-ground-deep space-y-4">
                      {turnstileError && (
                        <div className="p-3 bg-red-50 border border-red-200 text-red-800 text-xs text-center font-medium">
                          {turnstileError}
                        </div>
                      )}
                      <Turnstile
                        onVerify={(token) => { setTurnstileToken(token); setTurnstileError(null); }}
                        onExpire={() => { setTurnstileToken(''); setTurnstileError('Verification failed, please try again'); setResetKey(prev => prev + 1); }}
                        onError={() => { setTurnstileToken(''); setTurnstileError('Verification failed, please try again'); setResetKey(prev => prev + 1); }}
                        resetKey={resetKey}
                      />
                    </div>
                  )}

                  <div className="mt-10 pt-6 border-t border-ground-deep flex items-center justify-between">
                    {step > 1 ? (
                      <button
                        type="button"
                        onClick={handlePrev}
                        className="text-ink/70 hover:text-ink font-bold text-sm tracking-widest uppercase flex items-center gap-2 transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" /> Back
                      </button>
                    ) : <div></div>}

                    {step < totalSteps ? (
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={(step === 1 && !formData.role) || (isTeamFacilitator && !formData.hasCompletedLevel1)}
                        className="bg-ink text-white hover:bg-rule-3/60 hover:text-white py-3 px-6 text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Continue <ChevronRight className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting || !turnstileToken || (isTeamFacilitator && !formData.hasCompletedLevel1)}
                        className="bg-rule-3 text-white hover:text-ink hover:bg-white border border-transparent hover:border-rule-3 py-3 px-8 text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? t('registration.submitting', 'Submitting...') : (!turnstileToken ? t('registration.verifying', 'Verifying…') : t('registration.complete', 'Complete Registration'))} 
                        {!isSubmitting && turnstileToken && <Check className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <div className="w-20 h-20 bg-rule-3/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-rule-3" />
                </div>
                <h2 className="text-3xl font-serif text-ink mb-4">Registration Received</h2>
                <p className="text-ink/70 mb-8 max-w-md mx-auto leading-relaxed">
                  {emailSent ? (
                    <>Thank you for registering, {formData.firstName}. We've sent a confirmation email to <strong>{formData.email}</strong> and we confirm your intake date within five working days.</>
                  ) : (
                    <>Thank you for registering, {formData.firstName}. Your registration has been recorded and we confirm your intake date within five working days. If you have any immediate questions, please email office@enneainternational.com.</>
                  )}
                </p>
                <Button variant="outline" className="border-ink text-ink" onClick={() => navigate('/')}>
                  Return to Home
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}
